import { z } from 'zod';
import {
  GUIDANCE_COMPONENTS,
  GUIDANCE_STATES,
  anchorSpecSchema,
  triggerRuleSchema,
  guidanceContentSchema,
  DISMISSAL_MEMORIES,
} from '../configuration/guidance.schema.js';

export const journeySummaryDto = z.object({
  id: z.string().uuid(),
  name: z.string(),
  tracked: z.boolean(),
  stepCount: z.number().int(),
  sessionCount: z.number().int(),
  completionRate: z.number().min(0).max(1),
  guidanceCount: z.number().int(),
  lastAnalysedAt: z.string().datetime().nullable(),
});
export type JourneySummary = z.infer<typeof journeySummaryDto>;

/** An element seen on the step by the measurement: the only anchors a guidance may target (technical §7.2). */
export const observedElementDto = z.object({
  selectors: z.array(z.string()),
  elementType: z.string(),
  label: z.string().nullable(),
  role: z.string().nullable(),
});
export type ObservedElementDto = z.infer<typeof observedElementDto>;

/**
 * Identity of an observed element for the Console, which cannot import the
 * domain: the same rule as the domain's `elementKey`, pinned to the same
 * format by both test suites. The selector when the measurement could name the
 * element; its label together with the kind of control otherwise; nothing when
 * neither was recorded.
 */
export function observedElementKey(element: {
  readonly selectors: readonly string[];
  readonly elementType: string | null;
  readonly label: string | null;
}): string | null {
  const [selector] = element.selectors;
  if (selector) return selector;
  return element.label ? `${element.elementType ?? 'unknown'}|${element.label}` : null;
}

export const journeyStepDto = z.object({
  id: z.string().uuid(),
  name: z.string(),
  position: z.number().int(),
  pagePathPattern: z.string(),
  stepSignatures: z.array(z.string()),
  elements: z.array(observedElementDto),
  sessionCount: z.number().int(),
  dropOffRate: z.number().min(0).max(1),
  frictionScore: z.number().min(0).max(100),
  medianDurationMs: z.number().int(),
  staleSince: z.string().datetime().nullable(),
});
export type JourneyStepDto = z.infer<typeof journeyStepDto>;

export const journeyTransitionDto = z.object({
  fromStepId: z.string().uuid(),
  toStepId: z.string().uuid(),
  sessionCount: z.number().int(),
});
export type JourneyTransitionDto = z.infer<typeof journeyTransitionDto>;

export const guidanceDto = z.object({
  id: z.string().uuid(),
  journeyId: z.string().uuid(),
  stepId: z.string().uuid(),
  state: z.enum(GUIDANCE_STATES),
  component: z.enum(GUIDANCE_COMPONENTS),
  anchor: anchorSpecSchema.nullable(),
  trigger: triggerRuleSchema,
  dismissalMemory: z.enum(DISMISSAL_MEMORIES),
  reinforced: z.boolean(),
  contents: z.record(guidanceContentSchema),
  rationale: z.string(),
  expectedImpact: z.string(),
  currentVersion: z.number().int(),
  rowVersion: z.number().int(),
  updatedAt: z.string().datetime(),
});
export type GuidanceDto = z.infer<typeof guidanceDto>;

/** Full graph payload backing the interactive lineage view. */
export const journeyGraphDto = z.object({
  journey: journeySummaryDto,
  steps: z.array(journeyStepDto),
  transitions: z.array(journeyTransitionDto),
  guidances: z.array(guidanceDto),
});
export type JourneyGraph = z.infer<typeof journeyGraphDto>;

/** The only mutations the console may perform on a journey (doc technique §10.4). */
export const updateJourneyDto = z.object({
  name: z.string().min(1).max(120).optional(),
  tracked: z.boolean().optional(),
});

export const renameStepDto = z.object({ name: z.string().min(1).max(120) });
export const groupStepsDto = z.object({ stepIds: z.array(z.string().uuid()).min(2) });
